// @givmo/app-charity — tolerant decoders for the claim flow (CPA-WEB2).
//
// The api-client envelope middleware unwraps the paginated `{data,meta,links}` shape, so a list
// endpoint's SDK return is the bare row array at runtime even where the generated type names the
// envelope. The `/charity-admin/*` list endpoints (`/claims`, `/claims/{id}/documents`) return a
// NAMED wrapper (`{requests:[…]}` / `{documents:[…]}`) that the envelope middleware does NOT unwrap
// (it is shape-gated on `data` + `meta|links`). So we decode tolerantly via the shared `extractList`
// (`@givmo/api-client/list`), passing the wrapper's NAMED key so it accepts the named wrapper, a bare
// array, or the un-unwrapped `{data}` envelope, and never lets a surprising body shape break a screen.

import { extractList } from '@givmo/api-client/list';
import type {
  CharityOut,
  CharityClaimRequestOut,
  VerificationDocumentOut,
} from '@givmo/api-client';
import { isPendingStatus } from './claim-vocab';

/** Charities from `GET /charities?q=` (envelope-unwrapped to a bare `CharityOut[]`, tolerated). */
export function extractCharities(body: unknown): CharityOut[] {
  // `/charities` is the paginated envelope → the middleware unwraps to `CharityOut[]`. Guard the
  // rare un-unwrapped case (`{data}`) and unexpected shapes.
  return extractList<CharityOut>(body);
}

/** Claims from `GET /charity-admin/claims` (the `CharityClaimRequestListOut` `{requests:[…]}` wrapper). */
export function extractClaims(body: unknown): CharityClaimRequestOut[] {
  return extractList<CharityClaimRequestOut>(body, 'requests');
}

/** Verification docs from `GET …/documents` (the `VerificationDocumentListOut` `{documents:[…]}` wrapper). */
export function extractDocuments(body: unknown): VerificationDocumentOut[] {
  return extractList<VerificationDocumentOut>(body, 'documents');
}

/** Format an EIN for display (`123456789` → `12-3456789`) when it is a bare 9-digit string. */
export function formatEin(ein: string | null | undefined): string {
  if (ein == null) return '';
  const digits = ein.replace(/\D/g, '');
  if (digits.length === 9) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return ein;
}

/**
 * The claim the caller is currently waiting on, if any — the newest still-awaiting-review request.
 *
 * The backend orders `/charity-admin/claims` newest-first, so the FIRST pending row is the live one.
 * An account can hold several claims over time (a rejected attempt, an approved org, a second org),
 * and only one of them is the thing the portal should be reporting on.
 */
export function findPendingClaim(
  claims: readonly CharityClaimRequestOut[],
): CharityClaimRequestOut | null {
  return claims.find((claim) => isPendingStatus(claim.status)) ?? null;
}

/** UTC-safe claim timeline date; malformed or missing values remain absent. */
export function formatClaimDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
